import { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import axiosInstance from '../utills/axiosInstance';

export const useAttendance = (empId) => {
    const [currentSession, setCurrentSession] = useState(null);
    const [showTimer, setShowTimer] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isBreak, setIsBreak] = useState(false);
    const timerRef = useRef(null);

    const fetchTodayStatus = async () => {
        if (!empId) return;
        try {
            const res = await axiosInstance.get(`/api/today/${empId}`);
            const attendance = res.data.data;

            setCurrentSession(attendance);

            const hasActiveSession = attendance?.checkin &&
                (!attendance.checkout || new Date(attendance.checkin) > new Date(attendance.checkout));

            if (hasActiveSession) {
                setShowTimer(true);
                const onBreak = !!attendance.breaks?.find(b => !b.end);
                setIsBreak(onBreak);
            } else {
                setShowTimer(false);
                setIsBreak(false);
            }
        } catch (err) {
            console.error("Failed to fetch today status", err);
        }
    };

    useEffect(() => {
        if (!showTimer || !currentSession?.checkin) {
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }

        const calculateElapsed = () => {
            const checkinTime = moment(currentSession.checkin);
            const now = moment();

            let totalDiff = now.diff(checkinTime, 'seconds');

            currentSession.breaks?.forEach(br => {
                if (br.end) {
                    totalDiff -= moment(br.end).diff(moment(br.start), 'seconds');
                }
            });

            const activeBreak = currentSession.breaks?.find(b => !b.end);
            if (activeBreak) {
                const breakSoFar = now.diff(moment(activeBreak.start), 'seconds');
                totalDiff -= breakSoFar;
            }

            return Math.max(0, totalDiff);
        };

        setElapsedTime(calculateElapsed());

        timerRef.current = setInterval(() => {
            setElapsedTime(calculateElapsed());
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, [showTimer, currentSession, isBreak]);

    const handleCheckin = async () => {
        try {
            const res = await axiosInstance.post('/api/checkin', { empId });
            const attendance = res.data.data;
            setCurrentSession(attendance);
            setShowTimer(true);
            setIsBreak(false);
        } catch (err) {
            alert(err.response?.data?.error || 'Checkin failed');
        }
    };

    const handleToggleBreak = async () => {
        try {
            const isStartingBreak = !isBreak;
            const res = await axiosInstance.post('/api/break-resume', { empId, isBreak: isStartingBreak });
            const attendance = res.data.data;
            setCurrentSession(attendance);
            setIsBreak(!!attendance.breaks?.find(b => !b.end));
        } catch (err) {
            alert(err.response?.data?.error || 'Break/Resume failed');
        }
    };

    const handleCheckout = async () => {
        try {
            const res = await axiosInstance.post('/api/checkout', { empId });
            const attendance = res.data.data;
            setCurrentSession(attendance);
            setShowTimer(false);
            setIsBreak(false);
        } catch (err) {
            alert(err.response?.data?.error || 'Checkout failed');
        }
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
        const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    const hasTodayCheckout = currentSession?.checkout &&
        moment(currentSession.date).isSame(moment(), 'day') &&
        (!currentSession.checkin || new Date(currentSession.checkout) > new Date(currentSession.checkin));

    const canCheckin = !showTimer && !hasTodayCheckout;

    // Initial fetch
    useEffect(() => {
        fetchTodayStatus();
    }, [empId]);

    return {
        showTimer,
        elapsedTime,
        isBreak,
        formatTime,
        handleCheckin,
        handleToggleBreak,
        handleCheckout,
        canCheckin,
        hasTodayCheckout,
        fetchTodayStatus
    };
};
